import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, LogOut, Check, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const Stars = ({ rating }) => (
  <span className="flex gap-0.5 text-teal-500">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i < rating ? "fill-current" : "text-slate-200"}`} />
    ))}
  </span>
);

const StatusPill = ({ status }) => {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-teal-50 text-teal-700 border-teal-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([api.get("/admin/reviews"), api.get("/admin/enquiries")]);
      setReviews(r.data);
      setEnquiries(e.data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUser(data);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setReviews([]);
    setEnquiries([]);
  };

  const moderate = async (id, action) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { action });
      setReviews((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: action === "approve" ? "approved" : "rejected" } : r))
      );
      toast.success(action === "approve" ? "Review approved — it's now live." : "Review rejected.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="admin-loading">
        <RefreshCw className="h-6 w-6 animate-spin text-teal-700" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-slate-100 bg-white p-8 shadow-xl"
        >
          <div className="mb-8 flex items-center gap-3">
            <Logo className="h-10 w-10" />
            <div>
              <p className="font-serif text-xl font-semibold tracking-tight text-slate-900">Hyek Admin</p>
              <p className="text-xs font-semibold text-slate-400">Review moderation dashboard</p>
            </div>
          </div>
          <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={loginForm.email}
            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
          />
          <label htmlFor="admin-password" className="mt-4 block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-rose-600">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            className="mt-6 w-full rounded-full bg-teal-700 py-3.5 text-sm font-bold text-white transition-colors hover:bg-teal-600"
          >
            Sign In
          </button>
          <Link to="/" data-testid="admin-back-home-link" className="mt-4 block text-center text-xs font-semibold text-slate-400 hover:text-teal-700">
            ← Back to website
          </Link>
        </form>
      </div>
    );
  }

  const pending = reviews.filter((r) => r.status === "pending");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-serif text-lg font-semibold tracking-tight text-slate-900">Hyek Admin</p>
              <p className="text-[11px] font-semibold text-slate-400">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="text-xs font-bold text-slate-500 hover:text-teal-700">
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <section data-testid="admin-reviews-section">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-slate-900">
              Review moderation
            </h1>
            <span data-testid="admin-pending-count" className="rounded-full bg-amber-50 px-4 py-1.5 text-xs font-bold text-amber-700">
              {pending.length} awaiting approval
            </span>
          </div>

          {reviews.length === 0 ? (
            <p className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500" data-testid="admin-no-reviews">
              No reviews yet.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {reviews.map((r) => (
                <article
                  key={r.id}
                  data-testid={`admin-review-${r.id}`}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="max-w-2xl">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm font-bold text-slate-900">{r.name}</p>
                      <Stars rating={r.rating} />
                      <StatusPill status={r.status} />
                    </div>
                    <p className="mt-1 text-xs font-semibold text-slate-400">{r.service}</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">“{r.text}”</p>
                  </div>
                  {r.status === "pending" && (
                    <div className="flex shrink-0 gap-2">
                      <button
                        data-testid={`approve-review-${r.id}`}
                        onClick={() => moderate(r.id, "approve")}
                        className="flex items-center gap-1.5 rounded-full bg-teal-700 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-teal-600"
                      >
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        data-testid={`reject-review-${r.id}`}
                        onClick={() => moderate(r.id, "reject")}
                        className="flex items-center gap-1.5 rounded-full border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 transition-colors hover:bg-rose-50"
                      >
                        <X className="h-3.5 w-3.5" /> Reject
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="mt-14" data-testid="admin-enquiries-section">
          <h2 className="mb-6 font-serif text-3xl font-semibold tracking-tight text-slate-900">
            Quote enquiries
          </h2>
          {enquiries.length === 0 ? (
            <p className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500" data-testid="admin-no-enquiries">
              No enquiries yet.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {enquiries.map((e) => (
                <article key={e.id} data-testid={`admin-enquiry-${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-bold text-slate-900">
                      {e.name} <span className="font-semibold text-slate-400">· {e.service}</span>
                    </p>
                    <p className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  <p className="mt-1 text-xs font-semibold text-teal-700">
                    {e.email}{e.phone ? ` · ${e.phone}` : ""}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{e.message}</p>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
